import { captureException } from "@sentry/react";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import type { CalculatorPageState } from "web_myplann/Calculators/Types/CalculatorPageState";
import { CalculatorType } from "web_myplann/Calculators/Types/CalculatorType";
import { buildRefinancingPrefill } from "web_myplann/Calculators/Utils/buildRefinancingPrefill";
import { getNativeCalculatorScreen } from "web_myplann/Calculators/Utils/getNativeCalculatorScreen";
import { isInsuranceCalculator } from "web_myplann/Calculators/Utils/isInsuranceCalculator";
import { NATIVE_CALCULATOR_SCREENS } from "web_myplann/Calculators/Utils/nativeCalculatorScreens";
import type { PortfolioDetailResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { supportedNavigationTypesState } from "web_myplann/Shared/State/appInfoAtom";
import { device, NavigationTypes } from "web_myplann/Utils/device";
import type {
	CalculatorPrefill,
	NavigationType,
} from "web_myplann/Utils/WebViewModel";

export const useOpenCalculator = () => {
	const navigate = useNavigate();
	const hasGoalsRight = useGoalsRightOptional();
	const supportedNavigationTypes = useAtomValue(supportedNavigationTypesState);
	const [isOpeningCalculator, setIsOpeningCalculator] = useState(false);

	const openNativeScreen = async (
		screen: NavigationType,
		prefill?: CalculatorPrefill,
	): Promise<void> => {
		setIsOpeningCalculator(true);
		try {
			await device.navigationRequest(screen, undefined, prefill);
		} catch (error) {
			captureException(error);
		} finally {
			setIsOpeningCalculator(false);
		}
	};

	const openWebCalculator = (
		type: CalculatorType,
		state?: CalculatorPageState,
	) => navigate(Routing.Calculators.CalculatorDetail.path(type), { state });

	const openCalculator = async (type: CalculatorType): Promise<void> => {
		if (hasGoalsRight && !isInsuranceCalculator(type)) {
			openWebCalculator(type);
			return;
		}

		await openNativeScreen(
			getNativeCalculatorScreen(type, supportedNavigationTypes),
		);
	};

	const openRefinancingCalculator = async (
		portfolio: PortfolioDetailResponseDTO,
	): Promise<void> => {
		if (hasGoalsRight) {
			openWebCalculator(CalculatorType.Refinancing, {
				prefill: buildRefinancingPrefill(portfolio),
			});
			return;
		}

		const screen = getNativeCalculatorScreen(
			CalculatorType.Refinancing,
			supportedNavigationTypes,
		);
		const prefill =
			screen === NATIVE_CALCULATOR_SCREENS[CalculatorType.Refinancing]
				? buildRefinancingPrefill(portfolio)
				: null;

		await openNativeScreen(screen, prefill ?? undefined);
	};

	const openCalculatorList = async (): Promise<void> => {
		if (hasGoalsRight) {
			navigate(Routing.Calculators.CalculatorList.path());
			return;
		}

		await openNativeScreen(NavigationTypes.CalculatorListScreen);
	};

	return {
		openCalculator,
		openRefinancingCalculator,
		openCalculatorList,
		isOpeningCalculator,
	};
};
